import React, { Component, useEffect } from "react";
import withSizes from "react-sizes";
import MetaTags from 'react-meta-tags';

import { Header, SectionIntro, PageBanner, SectionCareer, Button } from "../../components";
import { useDispatch, useSelector } from 'react-redux'
import style from "./About.module.scss";
import pattern from "../../assets/patterns/pattern01.svg";
import useLocale from "../../hooks/useLocale";
import { loadAboutData } from "../../store/slices/about";
import { normalizeMedia, mapSeo } from "../../api/strapi";

function HookWrapper({ render }) {
  const locale = useLocale();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadAboutData(locale));
  }, [dispatch, locale]);

  return render(
    loadIntro(),
    loadBannerImage(),
    loadCompany(),
    loadTeam(),
    loadGallery(),
    loadGlossary(),
    loadSeo(),
    locale,
  );
}

function loadGlossary() {
  const { data } = useSelector(state => state.global)
  return (data && data.glossary) || {}
}

function loadIntro() {
  const { data } = useSelector(state => state.about)
  const company = data && data.companySection
  const banner = data && data.banner
  // Prefer banner for the top intro; fall back to company section text.
  return {
    title: (banner && banner.bannerTitle) || (company && company.title) || '',
    caption: (banner && banner.bannerDescription) || (company && company.description) || '',
  }
}

function loadBannerImage() {
  const { data } = useSelector(state => state.about)
  const banner = data && data.banner
  return (normalizeMedia(banner && banner.bannerImage) || {}).file_url || null
}

function loadCompany() {
  const { data } = useSelector(state => state.about)
  const company = data && data.companySection
  if (!company) return null
  return {
    title: company.title,
    caption: company.description,
    image: (normalizeMedia(company.image) || {}).file_url,
  }
}

function loadTeam() {
  const { data } = useSelector(state => state.about)
  const team = data && data.teamSection
  return {
    title: team && team.title,
    caption: team && team.description,
    image: (normalizeMedia(team && team.image) || {}).file_url,
  }
}

function loadGallery() {
  const { data } = useSelector(state => state.about)
  const gallery = (data && data.gallery) || []
  // Supports both the new repeatable gallery-slide component and the old
  // multi-media array shape, so existing entries keep rendering if still present.
  return gallery
    .map((item, i) => {
      const media = item && item.image ? item.image : item
      const file = normalizeMedia(media)
      return file && file.file_url ? { id: (item && item.id) || i + 1, image: file.file_url } : null
    })
    .filter(Boolean)
}

function loadSeo() {
  const { data } = useSelector(state => state.about)
  return mapSeo(data && data.seo)
}

class About extends Component {
  render() {
    const { isMobile } = this.props;

    return (
      <HookWrapper render={(intro, bannerImage, company, team, gallery, glossary, seo, locale) =>
        <>
          <MetaTags>
            <title>{seo.title}</title>
            <meta name="description" content={seo.meta_description} />
            <meta name="keyword" content={seo.meta_keyword} />
            <meta property="og:title" content={seo.title} />
          </MetaTags>
          <Header />
          <SectionIntro title={intro.title} caption={intro.caption} image={bannerImage} pattern={pattern} />

          {company && (company.image || company.title || company.caption) && (
            <section className={style.company__intro}>
              <div className={style.company__inner}>
                {company.image && (
                  <div className={style.company__image__wrap}>
                    <img src={company.image} className={style.company__image} alt="" />
                  </div>
                )}
                <div className={style.company__info}>
                  {company.title && <div className={style.company__title}>{company.title}</div>}
                  {company.caption && <p className={style.company__caption}>{company.caption}</p>}
                </div>
              </div>
            </section>
          )}

          {gallery.length > 0 && <PageBanner banner={gallery} />}

          <section className={style.team__intro}>
            <div className={style.team__inner}>
              <div className={`${style[`team__container--image`]}`}>
                <div className={`${style.team__image__container}`}>
                  <img src={team.image || '/assets/team-intro.png'} className={style.team__image} alt="" />
                </div>
              </div>
              <div className={style.team__info}>
                <div className={style.team__title}>{team.title || ''}</div>
                <p className={style.team__caption}>{team.caption || ''}</p>
                <div className={style.button__panel}>
                  <Button type="redirect" link={`/${locale}/team`} full={isMobile} buttonName={ glossary.ourTeam|| 'Our Team'} />
                </div>
              </div>
            </div>
          </section>
          <SectionCareer locale={locale} />
        </>
      } />
    )
  }
}

const mapStateToProps = ({ width }) => ({
  isMobile: width <= 500
});

export default withSizes(mapStateToProps)(About);
